import apiClient from './client';

export interface DeviceVPNInfo {
  device_id: string;
  device_name: string;
  is_hosting_enabled: boolean;
  has_wireguard_keys: boolean;
  vpn_ip: string | null;
  active_connections: number;
}

export interface VPNStatusResponse {
  sharing_enabled: boolean;
  has_wireguard_keys: boolean;
  vpn_ip?: string;
  active_connections: number;
  max_concurrent_connections: number;
  total_sessions_hosted: number;
  total_sessions_consumed: number;
  bandwidth_limit_mbps?: number;
  daily_data_limit_gb?: number;
  devices: DeviceVPNInfo[];
}

export interface VPNEnableRequest {
  device_id?: string;
  max_concurrent_connections?: number;
  bandwidth_limit_mbps?: number;
  daily_data_limit_gb?: number;
}

export interface VPNDisableRequest {
  device_id?: string;
}

export interface VPNConnectionRequest {
  friend_code?: string;
  host_user_id?: string;
  host_device_id?: string;
  session_name?: string;
  bandwidth_limit_mbps?: number;
  data_limit_gb?: number;
  time_limit_minutes?: number;
}

export interface VPNConnectionResponse {
  session_id: string;
  host_user_display_name: string;
  client_ip: string;
  status: string;
  message: string;
}

export interface VPNConfigWithQRResponse {
  config: string;
  config_type: string;
  public_key: string;
  vpn_ip?: string;
  qr_code?: string;
  qr_code_svg?: string;
}

// Issue #268: Connection mode enum (single source of truth)
export enum ConnectionMode {
  DIRECT_P2P = 'direct_p2p',
  STUN_ASSISTED_P2P = 'stun_assisted_p2p',
  CONNECTION_NODE_SIGNALING = 'connection_node_signaling',
  UNKNOWN = 'unknown',
}

export interface VPNSessionInfo {
  session_id: string;
  session_name?: string;
  status: string;
  connection_mode?: ConnectionMode;  // Issue #268: Connection type visibility
  client_ip?: string;
  created_at: string;
  started_at?: string;
  ended_at?: string;
  bytes_uploaded: number;
  bytes_downloaded: number;
  duration_seconds: number;
  host_display_name: string;
  client_display_name: string;
}

export interface VPNSessionListResponse {
  hosted_sessions: VPNSessionInfo[];
  client_sessions: VPNSessionInfo[];
}

export interface VPNSessionActionRequest {
  action: 'approve' | 'reject' | 'disconnect';
  reason?: string;
}

// Bandwidth history types (Issue #492)
export type BandwidthTimeframe = '1h' | '24h' | '7d' | '30d';
export type BandwidthStep = '5m' | '15m' | '1h';

export interface BandwidthHistoryPoint {
  timestamp: string;
  bytes_sent: number;
  bytes_received: number;
}

export interface BandwidthHistoryResponse {
  session_id: string;
  start_time: string;
  end_time: string;
  step: string;
  data_points: BandwidthHistoryPoint[];
}

export const vpnApi = {
  getStatus: () => 
    apiClient.get<VPNStatusResponse>('/api/v1/vpn/status'),
    
  enableSharing: (data: VPNEnableRequest) =>
    apiClient.post<VPNStatusResponse>('/api/v1/vpn/enable', data),

  disableSharing: (data?: VPNDisableRequest) =>
    apiClient.post<VPNStatusResponse>('/api/v1/vpn/disable', data || {}),
    
  getServerConfig: () => 
    apiClient.get<VPNConfigWithQRResponse>('/api/v1/vpn/config'),
    
  requestConnection: (data: VPNConnectionRequest) => 
    apiClient.post<VPNConnectionResponse>('/api/v1/vpn/connect', data),
    
  getSessions: () => 
    apiClient.get<VPNSessionListResponse>('/api/v1/vpn/sessions'),
    
  performSessionAction: (sessionId: string, data: VPNSessionActionRequest) => 
    apiClient.post(`/api/v1/vpn/sessions/${sessionId}/action`, data),
    
  getSessionConfig: (sessionId: string) =>
    apiClient.get<VPNConfigWithQRResponse>(`/api/v1/vpn/sessions/${sessionId}/config`),

  // Bandwidth history from VictoriaMetrics (Issue #492)
  getBandwidthHistory: (sessionId: string, timeframe: BandwidthTimeframe = '1h', step: BandwidthStep = '5m') =>
    apiClient.get<BandwidthHistoryResponse>(`/api/v1/sessions/${sessionId}/bandwidth/history`, {
      params: { timeframe, step }
    }),
};